import { getLimit } from './appConfig';

/**
 * Сколько ждать загрузку записи: простой вместо общего предела (решение техлида 02.10.2026).
 *
 * 🔴 ЧТО БЫЛО. Загрузка шла одним запросом с пределом 120 с НА ВСЁ (`api/uploads.ts`). Запись на 3 минуты —
 * 3–4 МБ; на связи хуже ~280 кбит/с вверх она не уходила никогда: каждый повтор начинал с нуля и упирался в
 * те же 120 с, сколько бы байт ни успело уйти. Ученик 01.10.2026 (iPhone, сайт, файл 1,8 МБ) получил 17
 * обрывов подряд за 25 минут (`docs/CHECK-2026-10-02-MORNING-FINDINGS.md` п. 4).
 *
 * 🔴 ЧТО СТАЛО. Пока байты идут — ждём сколько угодно: общего предела нет. Обрываем, только если процент не
 * двигался дольше простоя (60 с). Медленная, но живая связь больше не причина начинать заново.
 *
 * ⚠️ БЕЗ ПРОЦЕНТА ПРОСТОЙ НЕ ПРИМЕНЯЕТСЯ. В приложении с закреплённым сертификатом файл уходит одним вызовом
 * сетевого слоя ОС, событий отправки нет вовсе (`uploadProgressWorks()` в `lib/nativeHttp.ts`): «простой»
 * сработал бы на любой загрузке дольше минуты. Там остаётся общий предел.
 *
 * Оба числа — с сервера (`limits.uploadIdleSeconds`, `limits.uploadTotalSeconds`), без сборки.
 */

/** Простой по умолчанию, секунд. */
export const UPLOAD_IDLE_SECONDS = 60;
/** Общий предел по умолчанию, секунд, — только там, где процента нет. */
export const UPLOAD_TOTAL_SECONDS = 120;

const seconds = (name: string, fallback: number, min: number, max: number) => {
  const v = getLimit(name, fallback);
  return v >= min && v <= max ? v : fallback;
};

export type UploadPatience =
  /** Процент есть: общего предела нет, обрыв по простою. */
  | { mode: 'idle'; idleMs: number; totalMs: 0 }
  /** Процента нет: только общий предел. */
  | { mode: 'total'; idleMs: 0; totalMs: number };

export function uploadPatience(percentKnown: boolean): UploadPatience {
  return percentKnown
    ? { mode: 'idle', idleMs: seconds('uploadIdleSeconds', UPLOAD_IDLE_SECONDS, 10, 600) * 1000, totalMs: 0 }
    : { mode: 'total', idleMs: 0, totalMs: seconds('uploadTotalSeconds', UPLOAD_TOTAL_SECONDS, 30, 1800) * 1000 };
}

/** Признак «оборвали по простою» на ошибке — чтобы известие серверу назвало причину своим словом. */
export const UPLOAD_IDLE_MARK = 'upload_idle';

/**
 * Сторож простоя: `touch()` на каждое событие процента, `onIdle` — если их не было дольше `idleMs`.
 *
 * ⚠️ СЧИТАЕТ ДВИЖЕНИЕ, А НЕ СОБЫТИЯ. Событие с тем же числом байт — не движение: иначе загрузка, застрявшая
 * на 40 %, но продолжающая слать «40 %», висела бы вечно.
 */
export function createIdleWatch(idleMs: number, onIdle: () => void): { touch: (loaded?: number) => void; stop: () => void } {
  let timer: ReturnType<typeof setTimeout> | undefined;
  let last = -1;
  let done = false;
  const arm = () => {
    if (timer !== undefined) clearTimeout(timer);
    if (done || idleMs <= 0) return;
    timer = setTimeout(() => {
      timer = undefined;
      if (done) return;
      done = true;
      onIdle();
    }, idleMs);
  };
  arm();
  return {
    touch(loaded?: number) {
      if (typeof loaded === 'number') {
        if (loaded <= last) return; // байт не прибавилось — простой продолжается
        last = loaded;
      }
      arm();
    },
    stop() {
      done = true;
      if (timer !== undefined) clearTimeout(timer);
      timer = undefined;
    },
  };
}
